import { defineStore } from 'pinia';
import facilityApi from "@/api/facilityApi.js";

export const useFacilityStore = defineStore('facilityStore', {
    state: () => ({
        facilities: [],
        facility: null,
        participantsByFacility: {}, // кеш участников: facilityId -> []

    }),

    actions: {
        async loadFacilities({ page = 1, page_size, search = '', status, customer, ordering, responsible } = {}) {
            try {
                const params = { page };
                if (page_size) params.page_size = page_size;
                if (search) params.q = search;           // важно: только q, и только если непусто
                if (status) params.status = status;       // ACTIVE | ARCHIVED | INACTIVE
                if (customer != null && String(customer).trim().length) {
                    params.customer = customer;           // пробрасываем дальше; API преобразует в customer_id
                }
                if (ordering) params.ordering = ordering;
                if (responsible !== undefined && responsible !== null && responsible !== '') {
                    params.responsible = responsible;
                }

                return await facilityApi.getFacilities(params); // { count, results }
            } catch (error) {
                console.error('Ошибка загрузки объектов:', error);
                throw error;
            }
        },

        async loadFacility(id) {
            try {
                this.facility = await facilityApi.getFacility(id);
            } catch (error) {
                console.error('Ошибка загрузки клиента:', error);
            }
        },
        async loadFacilityParticipants(facilityId, { force = false } = {}) {
            const id = Number(facilityId);
            if (!id) return;
            if (!this.participantsByFacility) this.participantsByFacility = {};

            if (!force && Array.isArray(this.participantsByFacility[id])) {
                return this.participantsByFacility[id];
            }
            try {
                const participants = await facilityApi.getFacilityParticipants(id);
                this.participantsByFacility[id] = Array.isArray(participants) ? participants : [];
                return this.participantsByFacility[id];
            } catch (error) {
                console.error('Ошибка загрузки участников объекта:', error);
                this.participantsByFacility[id] = [];
                throw error;
            }
        },
        async addFacilityParticipant(facilityId, { customer_id, role }) {
            const id = Number(facilityId);
            await facilityApi.addFacilityParticipant(id, { customer_id, role });
            // Перезагрузим кеш
            await this.loadFacilityParticipants(id, { force: true });
        },

        async deleteFacilityParticipant(facilityId, linkId) {
            const id = Number(facilityId);
            await facilityApi.deleteFacilityParticipant(id, linkId);
            // Перезагрузим кеш
            await this.loadFacilityParticipants(id, { force: true });
        },

        // Пакетное сохранение набора связей:
        // desired = Array<{ customer_id: number, role: 'CUSTOMER'|'GENERAL_CONTRACTOR'|'CONTRACTOR' }>
        async saveFacilityParticipants(facilityId, desired = []) {
            const id = Number(facilityId);
            if (!id) return;

            const current = await this.loadFacilityParticipants(id); // [{ id: link_id, customer_id, role }, ...]
            const key = (x) => `${x.customer_id}::${x.role}`;

            const currentByKey = new Map(current.map(x => [key(x), x]));
            const desiredByKey = new Map(desired.map(x => [key(x), x]));

            const toDelete = current.filter(x => !desiredByKey.has(key(x)));
            const toAdd = desired.filter(x => !currentByKey.has(key(x)));

            // Выполним удаления
            for (const del of toDelete) {
                await facilityApi.deleteFacilityParticipant(id, del.id);
            }
            // Добавления
            for (const add of toAdd) {
                await facilityApi.addFacilityParticipant(id, { customer_id: add.customer_id, role: add.role });
            }

            await this.loadFacilityParticipants(id, { force: true });
        },

        // Создание объекта + установка связей
        async createFacilityWithParticipants(payload, participants = []) {
            const created = await facilityApi.createFacility(payload);
            if (created?.id) {
                await this.saveFacilityParticipants(created.id, participants);
                // можно сразу обновить this.facility, если форма редиректит на детали
                this.facility = created;
            }
            return created;
        },

        // Обновление объекта + установка связей
        async updateFacilityWithParticipants(id, payload, participants = [], method = 'put') {
            const updated = await facilityApi.updateFacility(id, payload, method);
            await this.saveFacilityParticipants(id, participants);
            await this.loadFacility(id);
            return updated;
        },


        // Создание
        async addFacility(payload) {
            try {
                return await facilityApi.createFacility(payload);
            } catch (error) {
                console.error('Ошибка добавления объекта:', error);
                throw error;
            }
        },

        // Обновление (PUT или PATCH)
        async updateFacility(id, payload, method = 'put') {
            try {
                const data = await facilityApi.updateFacility(id, payload, method);
                await this.loadFacility(id);
                return data;
            } catch (error) {
                console.error('Ошибка обновления объекта:', error);
                throw error;
            }
        },

        // Основной путь архивации
        async archiveFacility(id) {
            try {
                const res = await facilityApi.archiveFacility(id);
                this.facility = null;
                return res;
            } catch (error) {
                console.error('Ошибка архивирования объекта:', error);
                throw error;
            }
        },
        async unarchiveFacility(id) {
            try {
                const res = await facilityApi.unarchiveFacility(id);
                await this.loadFacility(id);
                return res;
            } catch (error) {
                console.error('Ошибка разархивирования объекта:', error);
                throw error;
            }
        },

        async deleteFacility(id) {
            try {
                const res = await facilityApi.deleteFacility(id);
                this.facility = null;
                return res;
            } catch (error) {
                console.error('Ошибка удаления объекта (alias delete):', error);
                throw error;
            }
        },
        // Загрузка координат объектов (без пагинации)
        async loadFacilitiesCoordinates({ force = false } = {}) {
            try {
                if (!force && Array.isArray(this.facilitiesCoordinates) && this.facilitiesCoordinates.length) {
                    return this.facilitiesCoordinates;
                }
                const data = await facilityApi.getFacilitiesCoordinates();
                this.facilitiesCoordinates = Array.isArray(data)
                    ? data
                        .map(x => {
                            const latitude = Number(x.latitude);
                            const longitude = Number(x.longitude);
                            return (isFinite(latitude) && isFinite(longitude)) ? { ...x, latitude, longitude } : null;
                        })
                        .filter(Boolean)
                    : [];
                return this.facilitiesCoordinates;
            } catch (error) {
                console.error('Ошибка загрузки координат объектов:', error);
                this.facilitiesCoordinates = [];
                throw error;
            }
        },





    },

});
